/**
 * Generated by orval v7.11.2 🍺
 * Do not edit manually.
 * UIBFS File Service API
 * File upload, download, metadata, and lifecycle management endpoints.
 * OpenAPI spec version: 1.0.0
 */
import { useMutation, useQuery } from "@tanstack/react-query";
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  MutationFunction,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult,
} from "@tanstack/react-query";

import * as axios from "axios";
import type { AxiosError, AxiosRequestConfig, AxiosResponse } from "axios";

import type { APIResponse, FileResponseDTO, FileUploadRequest } from "./models";

/**
 * Accepts a multipart upload, stores the file, and returns the generated metadata.
 * @summary Upload a file
 */
export const uploadFile = (
  fileUploadRequest: FileUploadRequest,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<FileResponseDTO>> => {
  const formData = new FormData();
  formData.append(`file`, fileUploadRequest.file);
  if (fileUploadRequest.usernameOrEmail !== undefined) {
    formData.append(`usernameOrEmail`, fileUploadRequest.usernameOrEmail);
  }

  return axios.default.post(
    `https://leave.uibfs.or.ug/uib-api/files/upload`,
    formData,
    options,
  );
};

export const getUploadFileMutationOptions = <
  TError = AxiosError<APIResponse>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof uploadFile>>,
    TError,
    { data: FileUploadRequest },
    TContext
  >;
  axios?: AxiosRequestConfig;
}): UseMutationOptions<
  Awaited<ReturnType<typeof uploadFile>>,
  TError,
  { data: FileUploadRequest },
  TContext
> => {
  const mutationKey = ["uploadFile"];
  const { mutation: mutationOptions, axios: axiosOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, axios: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof uploadFile>>,
    { data: FileUploadRequest }
  > = (props) => {
    const { data } = props ?? {};

    return uploadFile(data, axiosOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type UploadFileMutationResult = NonNullable<
  Awaited<ReturnType<typeof uploadFile>>
>;
export type UploadFileMutationBody = FileUploadRequest;
export type UploadFileMutationError = AxiosError<APIResponse>;

/**
 * @summary Upload a file
 */
export const useUploadFile = <
  TError = AxiosError<APIResponse>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof uploadFile>>,
      TError,
      { data: FileUploadRequest },
      TContext
    >;
    axios?: AxiosRequestConfig;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof uploadFile>>,
  TError,
  { data: FileUploadRequest },
  TContext
> => {
  const mutationOptions = getUploadFileMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * Accepts a multipart upload, stores the file, and returns the generated metadata.
 * @summary Open File upload
 */
export const openUploadFile = (
  fileUploadRequest: FileUploadRequest,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<FileResponseDTO>> => {
  const formData = new FormData();
  formData.append(`file`, fileUploadRequest.file);
  if (fileUploadRequest.usernameOrEmail !== undefined) {
    formData.append(`usernameOrEmail`, fileUploadRequest.usernameOrEmail);
  }

  return axios.default.post(
    `https://leave.uibfs.or.ug/uib-api/files/open-upload`,
    formData,
    options,
  );
};

export const getOpenUploadFileMutationOptions = <
  TError = AxiosError<APIResponse>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof openUploadFile>>,
    TError,
    { data: FileUploadRequest },
    TContext
  >;
  axios?: AxiosRequestConfig;
}): UseMutationOptions<
  Awaited<ReturnType<typeof openUploadFile>>,
  TError,
  { data: FileUploadRequest },
  TContext
> => {
  const mutationKey = ["openUploadFile"];
  const { mutation: mutationOptions, axios: axiosOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, axios: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof openUploadFile>>,
    { data: FileUploadRequest }
  > = (props) => {
    const { data } = props ?? {};

    return openUploadFile(data, axiosOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type OpenUploadFileMutationResult = NonNullable<
  Awaited<ReturnType<typeof openUploadFile>>
>;
export type OpenUploadFileMutationBody = FileUploadRequest;
export type OpenUploadFileMutationError = AxiosError<APIResponse>;

/**
 * @summary Open File upload
 */
export const useOpenUploadFile = <
  TError = AxiosError<APIResponse>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof openUploadFile>>,
      TError,
      { data: FileUploadRequest },
      TContext
    >;
    axios?: AxiosRequestConfig;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof openUploadFile>>,
  TError,
  { data: FileUploadRequest },
  TContext
> => {
  const mutationOptions = getOpenUploadFileMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * Returns the service health status
 * @summary Health check
 */
export const health = (
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<string>> => {
  return axios.default.get(`https://leave.uibfs.or.ug/uib-api/health`, {
    responseType: "text",
    ...options,
  });
};

export const getHealthQueryKey = () => {
  return [`https://leave.uibfs.or.ug/uib-api/health`] as const;
};

export const getHealthQueryOptions = <
  TData = Awaited<ReturnType<typeof health>>,
  TError = AxiosError<unknown>,
>(options?: {
  query?: Partial<
    UseQueryOptions<Awaited<ReturnType<typeof health>>, TError, TData>
  >;
  axios?: AxiosRequestConfig;
}) => {
  const { query: queryOptions, axios: axiosOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getHealthQueryKey();

  const queryFn: QueryFunction<Awaited<ReturnType<typeof health>>> = ({
    signal,
  }) => health({ signal, ...axiosOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof health>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type HealthQueryResult = NonNullable<Awaited<ReturnType<typeof health>>>;
export type HealthQueryError = AxiosError<unknown>;

export function useHealth<
  TData = Awaited<ReturnType<typeof health>>,
  TError = AxiosError<unknown>,
>(
  options: {
    query: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof health>>, TError, TData>
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof health>>,
          TError,
          Awaited<ReturnType<typeof health>>
        >,
        "initialData"
      >;
    axios?: AxiosRequestConfig;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData>;
};
export function useHealth<
  TData = Awaited<ReturnType<typeof health>>,
  TError = AxiosError<unknown>,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof health>>, TError, TData>
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof health>>,
          TError,
          Awaited<ReturnType<typeof health>>
        >,
        "initialData"
      >;
    axios?: AxiosRequestConfig;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useHealth<
  TData = Awaited<ReturnType<typeof health>>,
  TError = AxiosError<unknown>,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof health>>, TError, TData>
    >;
    axios?: AxiosRequestConfig;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
/**
 * @summary Health check
 */

export function useHealth<
  TData = Awaited<ReturnType<typeof health>>,
  TError = AxiosError<unknown>,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof health>>, TError, TData>
    >;
    axios?: AxiosRequestConfig;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = getHealthQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Retrieves the stored metadata for the given file identifier.
 * @summary Get file metadata
 */
export const getFileMetadata = (
  fileId: string,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<FileResponseDTO>> => {
  return axios.default.get(
    `https://leave.uibfs.or.ug/uib-api/files/get-metadata/${fileId}`,
    options,
  );
};

export const getGetFileMetadataQueryKey = (fileId?: string) => {
  return [
    `https://leave.uibfs.or.ug/uib-api/files/get-metadata/${fileId}`,
  ] as const;
};

export const getGetFileMetadataQueryOptions = <
  TData = Awaited<ReturnType<typeof getFileMetadata>>,
  TError = AxiosError<APIResponse>,
>(
  fileId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getFileMetadata>>,
        TError,
        TData
      >
    >;
    axios?: AxiosRequestConfig;
  },
) => {
  const { query: queryOptions, axios: axiosOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetFileMetadataQueryKey(fileId);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getFileMetadata>>> = ({
    signal,
  }) => getFileMetadata(fileId, { signal, ...axiosOptions });

  return {
    queryKey,
    queryFn,
    enabled: !!fileId,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<typeof getFileMetadata>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type GetFileMetadataQueryResult = NonNullable<
  Awaited<ReturnType<typeof getFileMetadata>>
>;
export type GetFileMetadataQueryError = AxiosError<APIResponse>;

export function useGetFileMetadata<
  TData = Awaited<ReturnType<typeof getFileMetadata>>,
  TError = AxiosError<APIResponse>,
>(
  fileId: string,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getFileMetadata>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getFileMetadata>>,
          TError,
          Awaited<ReturnType<typeof getFileMetadata>>
        >,
        "initialData"
      >;
    axios?: AxiosRequestConfig;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData>;
};
export function useGetFileMetadata<
  TData = Awaited<ReturnType<typeof getFileMetadata>>,
  TError = AxiosError<APIResponse>,
>(
  fileId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getFileMetadata>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getFileMetadata>>,
          TError,
          Awaited<ReturnType<typeof getFileMetadata>>
        >,
        "initialData"
      >;
    axios?: AxiosRequestConfig;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useGetFileMetadata<
  TData = Awaited<ReturnType<typeof getFileMetadata>>,
  TError = AxiosError<APIResponse>,
>(
  fileId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getFileMetadata>>,
        TError,
        TData
      >
    >;
    axios?: AxiosRequestConfig;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
/**
 * @summary Get file metadata
 */

export function useGetFileMetadata<
  TData = Awaited<ReturnType<typeof getFileMetadata>>,
  TError = AxiosError<APIResponse>,
>(
  fileId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getFileMetadata>>,
        TError,
        TData
      >
    >;
    axios?: AxiosRequestConfig;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = getGetFileMetadataQueryOptions(fileId, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Retrieves the raw file bytes for the supplied file identifier.
 * @summary Download a file
 */
export const downloadFile = (
  fileId: string,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<Blob>> => {
  return axios.default.get(
    `https://leave.uibfs.or.ug/uib-api/files/download/${fileId}`,
    {
      responseType: "blob",
      ...options,
    },
  );
};

export const getDownloadFileQueryKey = (fileId?: string) => {
  return [
    `https://leave.uibfs.or.ug/uib-api/files/download/${fileId}`,
  ] as const;
};

export const getDownloadFileQueryOptions = <
  TData = Awaited<ReturnType<typeof downloadFile>>,
  TError = AxiosError<string[]>,
>(
  fileId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof downloadFile>>, TError, TData>
    >;
    axios?: AxiosRequestConfig;
  },
) => {
  const { query: queryOptions, axios: axiosOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getDownloadFileQueryKey(fileId);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof downloadFile>>> = ({
    signal,
  }) => downloadFile(fileId, { signal, ...axiosOptions });

  return {
    queryKey,
    queryFn,
    enabled: !!fileId,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<typeof downloadFile>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type DownloadFileQueryResult = NonNullable<
  Awaited<ReturnType<typeof downloadFile>>
>;
export type DownloadFileQueryError = AxiosError<string[]>;

export function useDownloadFile<
  TData = Awaited<ReturnType<typeof downloadFile>>,
  TError = AxiosError<string[]>,
>(
  fileId: string,
  options: {
    query: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof downloadFile>>, TError, TData>
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof downloadFile>>,
          TError,
          Awaited<ReturnType<typeof downloadFile>>
        >,
        "initialData"
      >;
    axios?: AxiosRequestConfig;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData>;
};
export function useDownloadFile<
  TData = Awaited<ReturnType<typeof downloadFile>>,
  TError = AxiosError<string[]>,
>(
  fileId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof downloadFile>>, TError, TData>
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof downloadFile>>,
          TError,
          Awaited<ReturnType<typeof downloadFile>>
        >,
        "initialData"
      >;
    axios?: AxiosRequestConfig;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useDownloadFile<
  TData = Awaited<ReturnType<typeof downloadFile>>,
  TError = AxiosError<string[]>,
>(
  fileId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof downloadFile>>, TError, TData>
    >;
    axios?: AxiosRequestConfig;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
/**
 * @summary Download a file
 */

export function useDownloadFile<
  TData = Awaited<ReturnType<typeof downloadFile>>,
  TError = AxiosError<string[]>,
>(
  fileId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof downloadFile>>, TError, TData>
    >;
    axios?: AxiosRequestConfig;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = getDownloadFileQueryOptions(fileId, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Permanently removes the stored file and its metadata.
 * @summary Delete a file
 */
export const deleteFile = (
  fileId: string,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<APIResponse>> => {
  return axios.default.delete(
    `https://leave.uibfs.or.ug/uib-api/files/delete/${fileId}`,
    options,
  );
};

export const getDeleteFileMutationOptions = <
  TError = AxiosError<APIResponse>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteFile>>,
    TError,
    { fileId: string },
    TContext
  >;
  axios?: AxiosRequestConfig;
}): UseMutationOptions<
  Awaited<ReturnType<typeof deleteFile>>,
  TError,
  { fileId: string },
  TContext
> => {
  const mutationKey = ["deleteFile"];
  const { mutation: mutationOptions, axios: axiosOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, axios: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof deleteFile>>,
    { fileId: string }
  > = (props) => {
    const { fileId } = props ?? {};

    return deleteFile(fileId, axiosOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type DeleteFileMutationResult = NonNullable<
  Awaited<ReturnType<typeof deleteFile>>
>;

export type DeleteFileMutationError = AxiosError<APIResponse>;

/**
 * @summary Delete a file
 */
export const useDeleteFile = <
  TError = AxiosError<APIResponse>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof deleteFile>>,
      TError,
      { fileId: string },
      TContext
    >;
    axios?: AxiosRequestConfig;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof deleteFile>>,
  TError,
  { fileId: string },
  TContext
> => {
  const mutationOptions = getDeleteFileMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};
